import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, Copy, Check, Loader2, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Admin-only: confirm → generate a one-time temporary password for a user (shown once, copy button). */
const AdminResetPasswordDialog = ({ member, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const run = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/users/${member.id}/reset-password`, { confirm: true },
        { withCredentials: true, headers: { 'X-CivilX-Action': 'reset-password' } });
      setResult(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Reset failed');
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.temporary_password); setCopied(true); toast.success('Temporary password copied'); }
    catch { toast.error('Copy failed — select the password and copy it manually'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="admin-reset-dialog" onClick={result ? undefined : onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-900 inline-flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}><KeyRound className="w-4 h-4" /> Reset password — {member.name || member.email}</h3>
          <button data-testid="admin-reset-close" onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {!result ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>This generates a strong temporary password for <b>{member.email}</b>, signs the user out of <b>all devices</b>, and requires them to choose a new password at next sign-in. The user is notified by email (without the password). This action is recorded in the audit log.</span>
              </div>
              {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="admin-reset-error">{error}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
                <button data-testid="admin-reset-confirm" onClick={run} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00247D] disabled:opacity-50">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm reset
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-slate-600">Share this temporary password with the user <b>securely, in person or by phone</b>. It is shown <b>only once</b> and is not stored anywhere in CivilX.</p>
              <div className="flex items-center gap-2">
                <code data-testid="admin-reset-temp-password" className="flex-1 px-3 py-2 bg-slate-900 text-emerald-300 rounded-md text-base tracking-wider select-all break-all">{result.temporary_password}</code>
                <button data-testid="admin-reset-copy" onClick={copy} className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold border border-slate-300 rounded-md hover:bg-slate-50">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <ul className="text-[11px] text-slate-500 list-disc pl-4 space-y-0.5">
                <li>All existing sessions for this user were revoked.</li>
                <li>They must set a new password (12+ characters) before using CivilX again.</li>
              </ul>
              <div className="flex justify-end">
                <button data-testid="admin-reset-done" onClick={onClose} className="px-4 py-2 text-sm font-semibold bg-slate-900 text-white rounded-md">Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordDialog;
